import {
  ArrowRightIcon,
  Avatar,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  Text,
} from '@razorpay/blade/components';
import { EmployeeData } from 'api/gql-utils/user/types';
import { SkeletonTableBody } from 'components/SalaryComponents/components/SkeletonTableBody';
import { getPrefixedEmployee } from 'components/SalaryStructures/utils';

import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import React from 'react';

interface ComponentsTableProps {
  employees: EmployeeData[];
  currentPage: number;
  onPageSizeChange: ({ pageSize }: { pageSize: number }) => void;
  onPageChange: ({ page }: { page: number }) => void;
  pageSize: number;
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
}

const EmployeesTable: React.FC<ComponentsTableProps> = ({
  employees,
  currentPage,
  onPageSizeChange,
  onPageChange,
  pageSize,
  totalCount,
  isLoading,
  isFetching,
}) => {
  const isTableEmpty = employees.length === 0;
  if (!isLoading && !isFetching && isTableEmpty) {
    return <EmptyListView description="No Employee Found" title="Salary Structure" />;
  }
  return (
    <>
      {(isLoading || isFetching) && <SkeletonTableBody rowCount={6} columnCount={5} />}
      <Table
        data={{ nodes: employees }}
        showBorderedCells={false}
        display={'grid'}
        height={`${Math.min(350, 54 + 48 * employees?.length)}px`}
        gridTemplateColumns="2fr 3fr 1fr"
        rowDensity="normal"
        pagination={
          !isTableEmpty ? (
            <TablePagination
              showPageNumberSelector={true}
              showPageSizePicker={false}
              paginationType="server"
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              currentPage={currentPage}
              defaultPageSize={10}
              totalItemCount={totalCount}
            />
          ) : undefined
        }>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Name and ID</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Action</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {tableData.map((employee) => (
                <TableRow item={employee} key={employee.id}>
                  <TableCell>
                    <Box display="flex" gap="spacing.3" alignItems="center">
                      <Avatar name={employee.name} />
                      <Box>
                        <Text weight="semibold">{employee.name}</Text>
                        <Text size="xsmall" color="surface.text.gray.muted">
                          {getPrefixedEmployee(employee) || '--'}
                        </Text>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Text color="surface.text.gray.muted">
                      {employee.email || 'No Email available'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`/employeeDetail?userId=${employee?.peopleId}`}
                      target="_blank"
                      icon={ArrowRightIcon}
                      iconPosition="right">
                      Details
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        )}
      </Table>
      {<Box height={`${Math.max(350 - (54 + 48 * employees?.length))}px`}></Box>}
    </>
  );
};

export { EmployeesTable };
